/**
 * Exact numeric representation for physical measurements and quantities.
 *
 * Like money (integer cents), measurements that eventually feed quantity
 * and cost calculations are stored as integer HUNDREDTHS of their unit
 * (e.g. 1850.25 sqft -> 185025). Never binary floating point.
 *
 * Provenance (master spec Section 21): every stored measurement/quantity
 * carries a source. Phase 3 writes 'manual'; future systems add
 * 'geometry', 'assembly', 'override', 'imported', 'ai' — calculated
 * values and user overrides will coexist in separate columns rather than
 * overwriting one another.
 */

export type MeasurementSource =
  | "manual"
  /** Spec §90: a default that came with a package or renovation standard. */
  | "package"
  | "geometry"
  | "assembly"
  | "override"
  | "imported"
  | "ai";

export type MeasurementParseResult =
  | { ok: true; valueX100: number | null }
  | { ok: false; error: string };

/** Parse "1,850.25" -> 185025. Blank -> null ("not entered"). */
export function parseDecimalX100(raw: string): MeasurementParseResult {
  const cleaned = raw.replace(/[,\s]/g, "");
  if (cleaned === "") {
    return { ok: true, valueX100: null };
  }
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(cleaned);
  if (!match) {
    return { ok: false, error: "Enter a number like 1850 or 1,850.25." };
  }
  const [, whole, fraction] = match;
  const fracPadded = (fraction ?? "").padEnd(2, "0");
  const value =
    Number.parseInt(whole, 10) * 100 + (fracPadded ? Number.parseInt(fracPadded, 10) : 0);
  if (!Number.isSafeInteger(value)) {
    return { ok: false, error: "Value is too large." };
  }
  return { ok: true, valueX100: value };
}

/** Format 185025 -> "1,850.25" (trailing ".00" trimmed to "1,850"). */
export function formatX100(valueX100: number | null): string {
  if (valueX100 === null || valueX100 === undefined) {
    return "—";
  }
  const whole = Math.floor(valueX100 / 100)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const fraction = valueX100 % 100;
  return fraction === 0 ? whole : `${whole}.${fraction.toString().padStart(2, "0")}`;
}

/** Editable input string for a stored value: 185025 -> "1850.25", null -> "". */
export function x100ToInputString(valueX100: number | null): string {
  if (valueX100 === null || valueX100 === undefined) {
    return "";
  }
  const whole = Math.floor(valueX100 / 100).toString();
  const fraction = valueX100 % 100;
  return fraction === 0 ? whole : `${whole}.${fraction.toString().padStart(2, "0")}`;
}
